import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Loader2, KanbanSquare, Eye, CheckCircle2, Lock } from "lucide-react";
import MonthlyRevenue from "@/components/account/MonthlyRevenue";

const STAGE_STYLE = {
  DRAFT: "border-slate-200 bg-slate-50", SHARED: "border-indigo-200 bg-indigo-50/60",
  VIEWED: "border-violet-200 bg-violet-50/60", ACCEPTED_AWAITING_PAYMENT: "border-amber-200 bg-amber-50/60",
  PAID: "border-emerald-200 bg-emerald-50/60", REFUNDED: "border-orange-200 bg-orange-50/60",
  EXPIRED: "border-stone-200 bg-stone-50",
  CANCELLED: "border-red-200 bg-red-50/60",
};
const DOT = {
  DRAFT: "bg-slate-400", SHARED: "bg-indigo-500", VIEWED: "bg-violet-500",
  ACCEPTED_AWAITING_PAYMENT: "bg-amber-500", PAID: "bg-emerald-500", REFUNDED: "bg-orange-500", EXPIRED: "bg-stone-400", CANCELLED: "bg-red-500",
};

export default function Pipeline() {
  const [data, setData] = useState(undefined); // undefined=loading, null=forbidden/error
  const [status, setStatus] = useState(0);

  useEffect(() => {
    api.get("/proposals/pipeline")
      .then(({ data }) => setData(data))
      .catch((e) => { setStatus(e?.response?.status || 0); setData(null); });
  }, []);

  if (data === undefined)
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  if (data === null)
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <div className="text-center max-w-sm" data-testid="pipeline-forbidden">
          <Lock className="w-8 h-8 text-slate-300 mx-auto" />
          <p className="mt-3 font-semibold text-slate-700">Operators only</p>
          <p className="text-sm text-slate-500 mt-1">
            {status === 403
              ? "The sales pipeline is available to agency operators and administrators."
              : "We couldn't load the pipeline. Please sign in and try again."}
          </p>
          <Link to="/proposals" className="inline-block mt-4 text-indigo-600 font-medium">Go to proposals →</Link>
        </div>
      </div>
    );

  const cur = data.totals?.currency || "USD";

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="pipeline-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <KanbanSquare className="w-7 h-7 text-indigo-600" /> Sales pipeline
          </h1>
          <p className="text-slate-500 mt-1">
            {data.scope === "all" ? "Every proposal across the platform." : "Proposals you own."} Stages are derived from real, verified activity.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="pipeline-open-value">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Open pipeline</div>
            <div className="font-bold text-slate-900">{fmtMoney(data.totals?.open_pipeline_value, cur)}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5" data-testid="pipeline-paid-value">
            <div className="text-[11px] uppercase tracking-wide text-emerald-600">Paid (verified)</div>
            <div className="font-bold text-emerald-700">{fmtMoney(data.totals?.paid_value, cur)}</div>
          </div>
        </div>
      </div>

      <div className="mt-6"><MonthlyRevenue /></div>

      <div className="mt-8 flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
        {(data.stages || []).map((st) => (
          <div key={st.key} className={`shrink-0 w-[280px] rounded-2xl border ${STAGE_STYLE[st.key] || STAGE_STYLE.DRAFT}`} data-testid={`pipeline-col-${st.key}`}>
            <div className="p-4 border-b border-black/5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 font-heading font-semibold text-slate-900 text-sm">
                  <span className={`w-2 h-2 rounded-full ${DOT[st.key]}`} /> {st.label}
                </span>
                <span className="rounded-full bg-white/70 border border-black/5 px-2 py-0.5 text-xs font-bold text-slate-600" data-testid={`pipeline-count-${st.key}`}>{st.count}</span>
              </div>
              {st.total_amount > 0 && <p className="text-xs text-slate-500 mt-1">{fmtMoney(st.total_amount, cur)} total</p>}
            </div>
            <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
              {st.proposals.length === 0 && <p className="text-xs text-slate-400 px-1 py-4 text-center">No proposals</p>}
              {st.proposals.map((p) => (
                <Link key={p.public_id} to={`/proposals/${p.public_id}`} data-testid={`pipeline-card-${p.public_id}`}
                  className="block rounded-xl border border-slate-200 bg-white p-3 hover:border-indigo-300 hover:shadow-sm transition">
                  <p className="font-medium text-slate-900 text-sm truncate">{p.business_name || p.title}</p>
                  <p className="text-xs text-slate-400 truncate">{p.client_name || "No client name"}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900 text-sm">{fmtMoney(p.paid ? (p.paid_amount ?? p.amount) : p.amount, p.currency || cur)}</span>
                    <div className="flex items-center gap-2 text-[11px] text-slate-400">
                      {p.human_views > 0 && <span className="inline-flex items-center gap-0.5" title="Likely human views"><Eye className="w-3 h-3" /> {p.human_views}</span>}
                      {p.paid && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
